import type { ComponentMaintenanceData, MaintenanceActivity } from '../../../../Helpers/maintenanceCostUtils';

/**
 * Removing an activity row from a component in the draft.
 *
 * A row of one of the component type's own templates is shown whether or not
 * anything is stored for it, so removing it needs a marker
 * (`removedActivities/{templateId}: true`); a row added by hand from another
 * type's templates exists only as its stored entry. Either way the entry and
 * everything set on it is dropped. Adding the template again from the template
 * search gives a fresh row. Removing is the only way to take an activity off a
 * component: the `disabled` flag older drafts wrote is converted by the draft sync.
 */

/** Something the user set on the row that removing it loses for good */
export const activityHasOwnValues = (activity: MaintenanceActivity | undefined): boolean =>
    !!activity && (
        activity.costSource === 'custom'
        || activity.customUnitPrice !== undefined
        || !!activity.costComment
        || !!activity.comment
        || Object.keys(activity.costAttachments ?? {}).length > 0
        || activity.customQuantity !== undefined
        || activity.customUnitCount !== undefined
        || (activity.investmentPercentage ?? 0) > 0
    );

/** The type templates removed from the component, in the type's own order */
export const removedTypeTemplateIds = (
    entry: ComponentMaintenanceData | undefined,
    typeTemplateIds: string[]
): string[] => typeTemplateIds.filter(id => entry?.removedActivities?.[id] === true);

export const removeActivityFromEntry = <T extends ComponentMaintenanceData>(
    entry: T,
    templateId: string,
    isTypeTemplate: boolean
): T => {
    const { [templateId]: _dropped, ...maintenanceActivities } = entry.maintenanceActivities || {};
    const updated: T = { ...entry, maintenanceActivities };
    if (isTypeTemplate) {
        updated.removedActivities = { ...entry.removedActivities, [templateId]: true };
    }
    return updated;
};

/**
 * The template search picked a template: the row is on the component, as `activity`.
 * A removed row comes back fresh; a row already there is left as it is.
 */
export const addActivityToEntry = <T extends ComponentMaintenanceData>(
    entry: T,
    templateId: string,
    activity: MaintenanceActivity
): T => {
    const { [templateId]: wasRemoved, ...removed } = entry.removedActivities ?? {};
    if (entry.maintenanceActivities?.[templateId] && !wasRemoved) return entry;

    const updated: T = {
        ...entry,
        maintenanceActivities: {
            ...entry.maintenanceActivities,
            [templateId]: activity
        }
    };
    // Absent, not empty or undefined: the draft is saved as it is
    if (Object.keys(removed).length > 0) {
        updated.removedActivities = removed;
    } else {
        delete updated.removedActivities;
    }
    return updated;
};
